import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { Check, ChevronDown } from 'lucide-react';
import { Breed } from '../services/categoryService';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

interface BreedAutocompleteProps {
  breeds: Breed[];
  selectedBreed: Breed | null;
  onBreedSelect: (breed: Breed | null) => void;
  disabled?: boolean;
  loading?: boolean;
  placeholder?: string;
  className?: string;
}

export function BreedAutocomplete({
  breeds,
  selectedBreed,
  onBreedSelect,
  disabled = false,
  loading = false,
  placeholder = "Select breed...",
  className
}: BreedAutocompleteProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Filter breeds based on search query
  const filteredBreeds = useMemo(() => {
    if (!searchQuery.trim()) return breeds;
    
    return breeds.filter(breed =>
      breed.name.toLowerCase().includes(searchQuery.toLowerCase())
    );
  }, [breeds, searchQuery]);

  const handleBreedSelect = (breed: Breed) => {
    onBreedSelect(breed);
    setSearchQuery('');
    setOpen(false);
  };

  const handleClear = () => {
    onBreedSelect(null);
    setSearchQuery('');
  };

  if (loading) {
    return (
      <div className="space-y-2">
        <Label>{t('modals.addPet.breed')}</Label>
        <Skeleton className="h-10 w-full" />
      </div>
    );
  }

  return (
    <div className={cn("space-y-2", className)}>
      <Label htmlFor="breed">{t('modals.addPet.breed')}</Label>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={open}
            className={cn(
              "w-full justify-between text-left font-normal",
              disabled && "opacity-50 cursor-not-allowed",
              !selectedBreed && "text-muted-foreground"
            )}
            disabled={disabled}
          >
            {selectedBreed ? selectedBreed.name : placeholder}
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[var(--radix-popover-trigger-width)] max-w-none min-w-0 p-0 bg-background border shadow-lg z-50" align="start" sideOffset={4}>
          <div className="p-3">
            <Input
              placeholder={t('modals.addPet.selectBreed') || "Search breeds, Type first characters..."}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="mb-3 text-sm"
            />
            {selectedBreed && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="w-full justify-start mb-3 text-muted-foreground hover:bg-muted/50 text-xs"
              >
                {t('common.clear') || "Clear selection"}
              </Button>
            )}
          </div>
          <ScrollArea className="max-h-64">
            <div className="p-3 pt-0">
              {filteredBreeds.length === 0 ? (
                <div className="py-8 text-center text-sm text-muted-foreground">
                  {searchQuery ? (t('common.noResults') || 'No breeds found.') : (t('common.noDataAvailable') || 'No breeds available.')}
                </div>
              ) : (
                filteredBreeds.map((breed) => (
                  <Button
                    key={breed.id}
                    variant="ghost"
                    size="sm"
                    onClick={() => handleBreedSelect(breed)}
                    className={cn(
                      "w-full justify-start mb-1 text-sm hover:bg-muted/70 transition-colors",
                      selectedBreed?.id === breed.id && "bg-accent text-accent-foreground"
                    )}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4 transition-opacity",
                        selectedBreed?.id === breed.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    <span className="truncate">{breed.name}</span>
                  </Button>
                ))
              )}
            </div>
          </ScrollArea>
        </PopoverContent>
      </Popover>
    </div>
  );
}